import React, { useEffect, useState } from 'react';
import { userApi } from '../../services/api';
import { LegalDocument } from '../../types';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

function bumpVersion(version?: string | null): string {
  const raw = (version || '1.0').trim();
  const parts = raw.split('.');
  const last = Number(parts[parts.length - 1]);
  if (!Number.isFinite(last)) {
    return raw;
  }
  parts[parts.length - 1] = String(last + 1);
  return parts.join('.');
}

type Draft = {
  version: string;
  title: string;
  bodyHtml: string;
};

const emptyDraft: Draft = { version: '', title: '', bodyHtml: '' };

function fromDoc(doc?: LegalDocument | null): Draft {
  if (!doc) return { ...emptyDraft };
  return {
    version: doc.version || '',
    title: doc.title || '',
    bodyHtml: doc.bodyHtml || '',
  };
}

const DocumentEditor: React.FC<{
  kind: 'Terms' | 'Privacy';
  label: string;
  history: LegalDocument[];
  onSaved: () => void;
}> = ({ kind, label, history, onSaved }) => {
  const current = history.find((item) => item.isCurrent) || history[0] || null;
  const [draft, setDraft] = useState<Draft>(fromDoc(current));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 6000);
  useAutoClear(error, setError, 10000);

  useEffect(() => {
    setDraft(fromDoc(current));
  }, [current?.id, current?.version, current?.dateUpdated]);

  const publish = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await userApi.publishLegalDocument({
        documentType: kind,
        version: draft.version.trim(),
        title: draft.title.trim(),
        bodyHtml: draft.bodyHtml.trim(),
      });
      setMessage(response.data.message || `${label} published.`);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Could not publish ${label}.`);
    } finally {
      setSaving(false);
    }
  };

  const sameVersion = current?.version?.trim() === draft.version.trim();

  return (
    <Card className="p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{label}</h2>
        <p className="mt-1 text-sm text-gray-600">
          Keep the same version to correct the live text. Use a new version so signed-in users must accept again.
        </p>
      </div>
      <Input
        id={`${kind}-version`}
        label="Version"
        value={draft.version}
        onChange={(e) => setDraft({ ...draft, version: e.target.value })}
        placeholder="1.2"
      />
      {current?.version && (
        <button
          type="button"
          className="text-sm text-primary-700 hover:underline"
          onClick={() => setDraft({ ...draft, version: bumpVersion(current.version) })}
        >
          Suggest next version ({bumpVersion(current.version)})
        </button>
      )}
      <Input
        id={`${kind}-title`}
        label="Title"
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
      />
      <label className="block text-sm font-semibold text-gray-900" htmlFor={`${kind}-body`}>
        Body (HTML)
      </label>
      <textarea
        id={`${kind}-body`}
        className="mt-1 w-full min-h-[220px] rounded-md border-2 border-gray-300 px-4 py-2.5 text-sm font-mono text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
        value={draft.bodyHtml}
        onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" isLoading={saving} onClick={() => void publish()}>
          {sameVersion ? 'Update this version' : 'Publish new version'}
        </Button>
        <ActionStatus message={message} error={error} />
      </div>
      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Versions</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            {history.map((item) => (
              <li key={item.id}>
                <span className="font-medium text-gray-800">{item.version}</span>
                {item.isCurrent ? ' · current' : ''}
                {item.dateUpdated ? ` · ${new Date(item.dateUpdated).toLocaleDateString()}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

const LegalDocumentsAdminPage: React.FC = () => {
  const [terms, setTerms] = useState<LegalDocument[]>([]);
  const [privacy, setPrivacy] = useState<LegalDocument[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    userApi.listLegalDocuments()
      .then((r) => {
        setTerms(r.data.terms || []);
        setPrivacy(r.data.privacy || []);
      })
      .catch(() => setError('Could not load legal documents.'));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Terms & Privacy</h2>
        <p className="mt-1 text-sm text-gray-600">
          Owner only. Publishing a new version asks users to accept the updated document the next time they sign in.
        </p>
      </div>
      {error && <p className="text-sm text-error-600">{error}</p>}
      <div className="grid lg:grid-cols-2 gap-5">
        <DocumentEditor kind="Terms" label="Terms of Service" history={terms} onSaved={load} />
        <DocumentEditor kind="Privacy" label="Privacy Policy" history={privacy} onSaved={load} />
      </div>
    </div>
  );
};

export default LegalDocumentsAdminPage;
